import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Refresh,
  CheckCircle,
  DeleteOutline,
  OpenInNew,
  Inbox as InboxIcon,
  Assignment as RequestIcon,
  HelpOutline,
  Update as StatusIcon,
  QuestionAnswer as ClarifyIcon,
  Description as InfoIcon,
  DragIndicator,
  Language as PortalIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { praAPI } from '../services/api';

const EMAIL_TYPE_META = {
  information: {
    label: 'Has Records',
    color: 'success',
    icon: <InfoIcon fontSize="small" />,
  },
  status_update: {
    label: 'Status Update',
    color: 'info',
    icon: <StatusIcon fontSize="small" />,
  },
  clarification: {
    label: 'Needs Clarification',
    color: 'warning',
    icon: <ClarifyIcon fontSize="small" />,
  },
};

const EmailCard = ({
  email,
  onView,
  onMarkDone,
  onDiscard,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  isDragging,
  isDropTarget,
  onNavigateToRequest,
}) => {
  const typeMeta = EMAIL_TYPE_META[email.email_type] || null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      const now = new Date();
      const diffMs = now - d;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const extractSenderName = (fromStr) => {
    if (!fromStr) return 'Unknown';
    if (fromStr.includes('<')) {
      return fromStr.split('<')[0].trim().replace(/^"|"$/g, '');
    }
    return fromStr;
  };

  return (
    <Paper
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      elevation={0}
      sx={{
        p: 1.5,
        mb: 1,
        border: isDropTarget ? '2px dashed #2a52be' : '1px solid #e0ddd6',
        borderRadius: 1.5,
        cursor: 'grab',
        opacity: isDragging ? 0.4 : 1,
        backgroundColor: isDropTarget ? 'rgba(42,82,190,0.04)' : '#fff',
        transition: 'all 0.15s ease',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0,7,94,0.08)',
          borderColor: '#c0bdb5',
        },
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <DragIndicator sx={{ color: '#c8c4bb', fontSize: 16, mt: 0.25, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Subject */}
          <Typography
            onClick={() => onView(email)}
            sx={{
              fontFamily: 'Georgia, serif',
              fontWeight: 600,
              fontSize: '0.85rem',
              color: '#00075e',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {email.subject}
          </Typography>

          {/* From + date */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.25 }}>
            <Typography
              sx={{
                fontFamily: '"Helvetica Neue", Arial, sans-serif',
                fontSize: '0.75rem',
                color: '#838996',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '60%',
              }}
            >
              {extractSenderName(email.from)}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Helvetica Neue", Arial, sans-serif',
                fontSize: '0.7rem',
                color: '#c8c4bb',
                flexShrink: 0,
              }}
            >
              {formatDate(email.date)}
            </Typography>
          </Box>

          {/* Tags */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75, alignItems: 'center' }}>
            {email.is_nextrequest_item && (
              <Chip
                icon={<PortalIcon sx={{ fontSize: '0.65rem !important' }} />}
                label={`NR: ${email.nextrequest_id}`}
                size="small"
                sx={{ fontSize: '0.65rem', height: 18, backgroundColor: '#e8eaf6', color: '#3949ab' }}
              />
            )}
            {typeMeta && !email.is_nextrequest_item && (
              <Chip
                icon={typeMeta.icon}
                label={typeMeta.label}
                color={typeMeta.color}
                size="small"
                sx={{ fontSize: '0.65rem', height: 18 }}
              />
            )}
            {email.related_request_id && (
              <Chip
                label={email.related_request_subject || email.related_request_destination || `Request #${email.related_request_id}`}
                size="small"
                variant="outlined"
                onClick={() => onNavigateToRequest(email.related_request_id)}
                sx={{
                  fontSize: '0.65rem',
                  height: 18,
                  cursor: 'pointer',
                  maxWidth: 180,
                  '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                  '&:hover': { backgroundColor: 'rgba(42,82,190,0.06)' },
                }}
              />
            )}
          </Box>
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flexShrink: 0 }}>
          {email.is_nextrequest_item ? (
            <Tooltip title={email.nextrequest_url ? 'Check on NextRequest portal' : 'No portal URL recorded'}>
              <span>
                <IconButton
                  size="small"
                  component={email.nextrequest_url ? 'a' : 'button'}
                  href={email.nextrequest_url || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  disabled={!email.nextrequest_url}
                  sx={{ p: 0.25, color: '#3949ab', '&:hover': { color: '#2a52be' } }}
                >
                  <PortalIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <>
              <Tooltip title="Mark as done">
                <IconButton
                  size="small"
                  onClick={() => onMarkDone(email.id)}
                  sx={{ p: 0.25, color: '#838996', '&:hover': { color: '#2a52be' } }}
                >
                  <CheckCircle sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Archive (discard)">
                <IconButton
                  size="small"
                  onClick={() => onDiscard(email.id)}
                  sx={{ p: 0.25, color: '#838996', '&:hover': { color: '#d32f2f' } }}
                >
                  <DeleteOutline sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>
    </Paper>
  );
};


const Column = ({
  title,
  icon,
  emails,
  count,
  color,
  onView,
  onMarkDone,
  onDiscard,
  onNavigateToRequest,
  dragState,
  setDragState,
  onDropToColumn,
}) => {
  const isDropTargetColumn = dragState.dropColumn === title && dragState.draggedId !== null;

  return (
    <Box
      onDragOver={(e) => {
        e.preventDefault();
        if (dragState.draggedId && dragState.sourceColumn !== title) {
          setDragState((s) => ({ ...s, dropColumn: title }));
        }
      }}
      onDragLeave={(e) => {
        // Only clear if leaving the column entirely
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setDragState((s) => ({ ...s, dropColumn: null }));
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        if (dragState.draggedId && dragState.sourceColumn !== title) {
          onDropToColumn(dragState.draggedId, title);
        }
        setDragState({ draggedId: null, sourceColumn: null, dropColumn: null });
      }}
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1.5,
          pb: 1,
          borderBottom: `2px solid ${color}`,
        }}
      >
        <Box sx={{ color }}>{icon}</Box>
        <Typography
          sx={{
            fontFamily: '"Helvetica Neue", Arial, sans-serif',
            fontWeight: 700,
            fontSize: '0.8rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: '#00075e',
          }}
        >
          {title}
        </Typography>
        <Chip
          label={count}
          size="small"
          sx={{
            fontFamily: '"Helvetica Neue", Arial, sans-serif',
            fontWeight: 600,
            fontSize: '0.7rem',
            height: 18,
            backgroundColor: color,
            color: '#fff',
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 120,
          p: isDropTargetColumn ? 1 : 0,
          border: isDropTargetColumn ? `2px dashed ${color}` : '2px solid transparent',
          borderRadius: 1,
          transition: 'all 0.15s ease',
          backgroundColor: isDropTargetColumn ? `${color}10` : 'transparent',
        }}
      >
        {emails.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
              color: '#c8c4bb',
            }}
          >
            <Typography sx={{ fontFamily: '"Helvetica Neue", Arial', fontSize: '0.8rem' }}>
              No emails here
            </Typography>
          </Box>
        ) : (
          emails.map((email) => (
            <EmailCard
              key={email.id}
              email={email}
              onView={onView}
              onMarkDone={onMarkDone}
              onDiscard={onDiscard}
              onNavigateToRequest={onNavigateToRequest}
              onDragStart={email.is_nextrequest_item ? () => {} : () =>
                setDragState({ draggedId: email.id, sourceColumn: title, dropColumn: null })
              }
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => e.stopPropagation()}
              onDragEnd={() =>
                setDragState({ draggedId: null, sourceColumn: null, dropColumn: null })
              }
              isDragging={dragState.draggedId === email.id}
              isDropTarget={false}
            />
          ))
        )}
      </Box>
    </Box>
  );
};


const SenderInbox = () => {
  const navigate = useNavigate();
  const [emails, setEmails] = useState([]);
  const [nextrequestItems, setNextrequestItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [dragState, setDragState] = useState({
    draggedId: null,
    sourceColumn: null,
    dropColumn: null,
  });
  // Manual column overrides: { [emailId]: 'Open Requests' | 'Other Emails' }
  const [columnOverrides, setColumnOverrides] = useState({});

  useEffect(() => {
    fetchEmails();
  }, []);

  const fetchEmails = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/inbox/emails', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch emails');
      const data = await response.json();
      // Filter out discarded emails
      setEmails((data.emails || []).filter((e) => e.status !== 'discarded'));
      setNextrequestItems(data.nextrequest_items || []);
      if (data.message) setError(data.message); // show as info not error
    } catch (err) {
      setError(err.message || 'Failed to load emails');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await fetch('/api/inbox/sync', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchEmails();
    } catch (err) {
      setError('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateStatus = async (messageId, newStatus) => {
    setUpdatingStatus((prev) => ({ ...prev, [messageId]: true }));
    try {
      const token = localStorage.getItem('token');
      await fetch(`/api/inbox/emails/${messageId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (newStatus === 'discarded') {
        setEmails((prev) => prev.filter((e) => e.id !== messageId));
      } else {
        setEmails((prev) =>
          prev.map((e) => (e.id === messageId ? { ...e, status: newStatus } : e))
        );
      }
    } catch (err) {
      setError('Failed to update email status');
    } finally {
      setUpdatingStatus((prev) => ({ ...prev, [messageId]: false }));
    }
  };

  const handleViewEmail = async (email) => {
    setSelectedEmail(email);
    setEmailDialogOpen(true);
    // Fetch full body in background
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/inbox/emails/${email.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedEmail((prev) => ({ ...prev, ...data }));
      }
    } catch {
      // ignore, we show snippet
    }
  };

  // Drag a card from one column to another
  const handleDropToColumn = (emailId, targetColumn) => {
    setColumnOverrides((prev) => ({ ...prev, [emailId]: targetColumn }));
  };

  // Determine which column an email belongs to
  const getEmailColumn = (email) => {
    if (columnOverrides[email.id]) return columnOverrides[email.id];
    if (email.is_pra_related && email.related_request_id) return 'Open Requests';
    return 'Other Emails';
  };

  const requestEmails = [
    ...nextrequestItems,
    ...emails.filter((e) => getEmailColumn(e) === 'Open Requests' && e.status !== 'done'),
  ];
  const otherEmails = emails.filter((e) => getEmailColumn(e) === 'Other Emails' && e.status !== 'done');
  const doneEmails = emails.filter((e) => e.status === 'done');

  const columns = [
    {
      title: 'Open Requests',
      icon: <RequestIcon fontSize="small" />,
      emails: requestEmails,
      color: '#2a52be',
    },
    {
      title: 'Other Emails',
      icon: <InboxIcon fontSize="small" />,
      emails: otherEmails,
      color: '#838996',
    },
    {
      title: 'Done',
      icon: <CheckCircle fontSize="small" />,
      emails: doneEmails,
      color: '#4caf50',
    },
  ];

  return (
    <Container maxWidth="xl">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography
            variant="h4"
            sx={{ fontFamily: 'Georgia, serif', fontWeight: 700, color: '#00075e' }}
          >
            Sender Inbox
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Helvetica Neue", Arial, sans-serif',
              color: '#838996',
              fontSize: '0.85rem',
              mt: 0.5,
            }}
          >
            {emails.length} email{emails.length !== 1 ? 's' : ''}
          {nextrequestItems.length > 0 && ` · ${nextrequestItems.length} NextRequest submission${nextrequestItems.length !== 1 ? 's' : ''}`}
          {' · '}Drag cards between columns to reorganize
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={syncing ? <CircularProgress size={14} /> : <Refresh />}
          onClick={handleSync}
          disabled={syncing || loading}
        >
          {syncing ? 'Syncing…' : 'Sync Inbox'}
        </Button>
      </Box>

      {error && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          {columns.map((col) => (
            <Column
              key={col.title}
              title={col.title}
              icon={col.icon}
              emails={col.emails}
              count={col.emails.length}
              color={col.color}
              onView={handleViewEmail}
              onMarkDone={(id) => handleUpdateStatus(id, 'done')}
              onDiscard={(id) => handleUpdateStatus(id, 'discarded')}
              onNavigateToRequest={(id) => navigate(`/requests/${id}`)}
              dragState={dragState}
              setDragState={setDragState}
              onDropToColumn={handleDropToColumn}
            />
          ))}
        </Box>
      )}

      {/* Email detail dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedEmail && (
          <>
            <DialogTitle
              sx={{
                fontFamily: 'Georgia, serif',
                color: '#00075e',
                borderBottom: '1px solid #e0ddd6',
              }}
            >
              {selectedEmail.subject}
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '80px 1fr',
                  gap: 1,
                  mb: 2,
                  fontFamily: '"Helvetica Neue", Arial, sans-serif',
                  fontSize: '0.85rem',
                }}
              >
                <Typography sx={{ color: '#838996', fontFamily: 'inherit', fontSize: 'inherit' }}>
                  From:
                </Typography>
                <Typography sx={{ color: '#00075e', fontFamily: 'inherit', fontSize: 'inherit' }}>
                  {selectedEmail.from}
                </Typography>
                <Typography sx={{ color: '#838996', fontFamily: 'inherit', fontSize: 'inherit' }}>
                  Date:
                </Typography>
                <Typography sx={{ color: '#00075e', fontFamily: 'inherit', fontSize: 'inherit' }}>
                  {selectedEmail.date}
                </Typography>
                {selectedEmail.related_request_id && (
                  <>
                    <Typography sx={{ color: '#838996', fontFamily: 'inherit', fontSize: 'inherit' }}>
                      Request:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ color: '#00075e', fontFamily: 'inherit', fontSize: 'inherit' }}>
                        {selectedEmail.related_request_subject || selectedEmail.related_request_destination || `Request #${selectedEmail.related_request_id}`}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEmailDialogOpen(false);
                          navigate(`/requests/${selectedEmail.related_request_id}`);
                        }}
                      >
                        <OpenInNew fontSize="small" />
                      </IconButton>
                    </Box>
                  </>
                )}
                {selectedEmail.email_type && (
                  <>
                    <Typography sx={{ color: '#838996', fontFamily: 'inherit', fontSize: 'inherit' }}>
                      Type:
                    </Typography>
                    <Chip
                      label={EMAIL_TYPE_META[selectedEmail.email_type]?.label || selectedEmail.email_type}
                      color={EMAIL_TYPE_META[selectedEmail.email_type]?.color || 'default'}
                      size="small"
                    />
                  </>
                )}
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Typography
                component="pre"
                sx={{
                  fontFamily: 'Georgia, serif',
                  fontSize: '0.9rem',
                  color: '#00075e',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: 1.7,
                  backgroundColor: '#f9f8f5',
                  p: 2,
                  borderRadius: 1,
                }}
              >
                {selectedEmail.body || selectedEmail.snippet || '(No content)'}
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={() => {
                  handleUpdateStatus(selectedEmail.id, 'done');
                  setEmailDialogOpen(false);
                }}
                startIcon={<CheckCircle />}
                variant="outlined"
              >
                Mark Done
              </Button>
              <Button
                onClick={() => {
                  handleUpdateStatus(selectedEmail.id, 'discarded');
                  setEmailDialogOpen(false);
                }}
                startIcon={<DeleteOutline />}
                color="error"
                variant="outlined"
              >
                Archive
              </Button>
              <Button onClick={() => setEmailDialogOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default SenderInbox;
